import { UIMessage } from "@ai-sdk/react";
import SendIcon from "@mui/icons-material/Send";
import { Theme, Typography } from "@mui/material";
import { useUserInfo } from "@sans-service-tier/sdk-react";
import hljs from "highlight.js";
import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { AiSmeChat } from "../../hooks/useAiSmeChat";
import { useAnimatedLoadingMessage } from "../../hooks/useAnimatedLoadingMessage";
import { useHighlightJsTheme } from "../../hooks/useHighlightJsTheme";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { telemetryChannelName } from "../../util/constants";
import {
  AiAvatar,
  AiSpinner,
  BetaBadge,
  ChatHeaderContainer,
  ChatInputContainer,
  ChatMessageInput,
  ChatMessageInputContainer,
  ChatMessagesContainer,
  EmptyStateContainer,
  MessageBubble,
  MessageContainer,
  MessagesArea,
  SendChatMessageIconButton,
  StyledAiSparkleIcon,
  StyledErrorIcon,
  UserAvatar,
  UserMessageContainer,
} from "./Common.elements";
import { HelpAndSupportHeader } from "./HelpAndSupportHeader";
import { Title } from "./TechSupportMessageDialog.elements";

interface AiSmeChatDialogProps {
  readonly chat: AiSmeChat;
}

function messageHasVisibleText(message: UIMessage) {
  return message.parts.some(part => part.type === "text" && part.text && part.text.trim().length > 0);
}

function messageHasToolCalls(message: UIMessage) {
  return message.parts.some(part => part.type.startsWith("tool-"));
}

export function AiSmeChatDialog({ chat }: Readonly<AiSmeChatDialogProps>): React.ReactElement {
  const [input, setInput] = useState("");
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const animatedMessageSuffix = useAnimatedLoadingMessage(true, "");
  const isUserScrollingRef = useRef(false);

  const user = useUserInfo();
  const userInitial = useMemo(() => user?.given_name?.[0], [user]);

  const messages = chat.messages;
  const sendMessage = chat.sendMessage;

  useHighlightJsTheme();

  function resetChat() {
    chat.stop?.();
    chat.setMessages([]);
    setIsProcessingResponse(false);
    setInput("");
  }

  // Fire telemetry event when chat is opened
  useEffect(() => {
    createTelemetryEvent({
      feature: "ai sme chat",
      action: "opened",
    }).fire(telemetryChannelName);
    return () =>
      createTelemetryEvent({
        feature: "ai sme chat",
        action: "closed",
      }).fire(telemetryChannelName);
  }, []);

  // Handle chat errors - reset processing state and fire telemetry
  useEffect(() => {
    if (chat.error) {
      setIsProcessingResponse(false);

      // Fire telemetry event with error details
      createTelemetryEvent({
        feature: "ai sme chat",
        action: "error sending message",
        action_meta: chat.error.message || "Unknown error",
      }).fire(telemetryChannelName);
    }
  }, [chat.error]);

  // Handle chat finish - reset processing when status changes away from submitted/streaming
  useEffect(() => {
    if (chat.status !== "submitted" && chat.status !== "streaming") {
      setIsProcessingResponse(false);
    }
  }, [chat.status]);

  // Track user scroll behavior to determine if we should auto-scroll
  useEffect(() => {
    const scrollContainer = messagesEndRef.current?.parentElement;
    if (!scrollContainer) {
      return;
    }

    const isAtBottom = () => {
      return scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 30;
    };

    const handleMouseDown = () => {
      // User clicked - likely on scrollbar or content, immediately disable auto-scroll
      isUserScrollingRef.current = true;
    };

    const handleMouseUp = () => {
      // Re-enable auto-scroll only if user released at the bottom
      if (isAtBottom()) {
        isUserScrollingRef.current = false;
      }
    };

    const handleScroll = () => {
      // Update auto-scroll state based on scroll position
      isUserScrollingRef.current = !isAtBottom();
    };

    const handleWheel = () => {
      // Mouse wheel used - update auto-scroll state based on position
      isUserScrollingRef.current = !isAtBottom();
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    scrollContainer.addEventListener("mousedown", handleMouseDown);
    scrollContainer.addEventListener("mouseup", handleMouseUp);
    scrollContainer.addEventListener("wheel", handleWheel);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.removeEventListener("mousedown", handleMouseDown);
      scrollContainer.removeEventListener("mouseup", handleMouseUp);
      scrollContainer.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Auto-scroll to bottom only if user hasn't manually scrolled away
  useEffect(() => {
    if (!messagesEndRef.current || isUserScrollingRef.current) {
      return;
    }

    const scrollContainer = messagesEndRef.current.parentElement;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [messages, chat.status]);

  useEffect(() => {
    if (!messagesAreaRef.current) {
      return;
    }

    const codeBlocks = messagesAreaRef.current.querySelectorAll(
      "pre[class*='language-'] code, pre code[class*='language-'], pre code.highlight",
    );

    codeBlocks.forEach(block => {
      const codeContent = block.textContent ?? "";
      block.textContent = codeContent;
      delete (block as HTMLElement).dataset.highlighted;
      hljs.highlightElement(block as HTMLElement);
    });
  }, [messages]);

  function handleSendMessage(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = input.trim();
    if (!value || isProcessingResponse) {
      return;
    }

    // Clear any previous errors before sending new message
    if (chat.error) {
      chat.clearError?.();
    }

    setIsProcessingResponse(true);
    sendMessage({ text: value });
    setInput("");

    // Fire telemetry event when message is sent
    createTelemetryEvent({
      feature: "ai sme chat",
      action: "send message",
    }).fire(telemetryChannelName);
  }

  function handleTryAgain() {
    // Find the last user message to retry
    const lastUserMessage = [...messages].reverse().find(msg => msg.role === "user");

    if (!lastUserMessage) {
      // If no user message found, just clear the error
      chat.clearError?.();
      setIsProcessingResponse(false);
      return;
    }

    // Extract the text content from the message
    const messageText = lastUserMessage.parts
      .filter(part => part.type === "text")
      .map(part => part.text)
      .join("");

    // Capture error message for telemetry
    const errorMessage = chat.error?.message || "Unknown error";

    // Clear the error state
    chat.clearError?.();

    // Resend the last message
    setIsProcessingResponse(true);
    sendMessage({ text: messageText });

    // Fire telemetry event with error details
    createTelemetryEvent({
      feature: "ai sme chat",
      action: "retry send message after error",
      action_meta: `previous error was: ${errorMessage}`,
    }).fire(telemetryChannelName);
  }

  function handleCloseChat() {
    resetChat();
  }

  return (
    <>
      <ChatHeaderContainer>
        <HelpAndSupportHeader onClose={handleCloseChat}>
          <div className="header-wrapper">
            <Title className="tech-support-header">
              AI SME Chat <BetaBadge mode="dark">BETA</BetaBadge>
            </Title>
          </div>
        </HelpAndSupportHeader>
      </ChatHeaderContainer>

      <ChatMessagesContainer>
        <MessagesArea ref={messagesAreaRef}>
          {/* Initial state shown */}
          {!messages.length && !isProcessingResponse && !chat.error && (
            <EmptyStateContainer>
              <StyledAiSparkleIcon />
              <Typography color="text.primary" variant="body2">
                Need an explanation? Unsure about a specific term? Need practical examples? Need more details?
              </Typography>
            </EmptyStateContainer>
          )}

          {/* Generic state shown when any error is received (no messages yet) */}
          {chat.error && !messages.length && (
            <EmptyStateContainer>
              <StyledErrorIcon />
              <Typography color="text.primary" variant="body2">
                An error occurred while processing your request. Please try again.
              </Typography>
            </EmptyStateContainer>
          )}

          {/* Where messages do exist, iterate through and display each message */}
          {messages.map((message: UIMessage) => {
            const isUser = message.role === "user";
            const MessageContainerComponent = isUser ? UserMessageContainer : MessageContainer;
            const isAiProcessing = !isUser && !messageHasVisibleText(message);

            let avatarElement;
            if (isUser) {
              avatarElement = <UserAvatar>{userInitial}</UserAvatar>;
            } else if (isAiProcessing) {
              avatarElement = <AiSpinner size={24} />;
            } else {
              avatarElement = (
                <AiAvatar>
                  <StyledAiSparkleIcon sx={{ fontSize: 16, color: "white" }} />
                </AiAvatar>
              );
            }

            return (
              <MessageContainerComponent key={message.id}>
                {avatarElement}
                <MessageBubble className={isUser ? "user-message" : "ai-message"}>
                  {/* Show typing indicator when AI is processing */}
                  {!isUser && !messageHasVisibleText(message) && (
                    <div className="chat-typing">
                      <span className="chat-typing-text">
                        {messageHasToolCalls(message) ? "Searching" : "Thinking"}
                        {animatedMessageSuffix}
                      </span>
                    </div>
                  )}

                  {/* Display the message parts */}
                  {message.parts.map(
                    (part: UIMessage["parts"][number], partIndex: number) =>
                      part.type === "text" && (
                        <div key={`${message.id}-${partIndex}`}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.text ?? ""}</ReactMarkdown>
                        </div>
                      ),
                  )}
                </MessageBubble>
              </MessageContainerComponent>
            );
          })}

          {/* Show typing indicator when AI is processing (but not if there's an error) */}
          {chat.status === "submitted" && !chat.error && (
            <MessageContainer>
              <AiSpinner size={24} />
              <MessageBubble className="ai-message">
                <div className="chat-typing">
                  <span className="chat-typing-text">Thinking{animatedMessageSuffix}</span>
                </div>
              </MessageBubble>
            </MessageContainer>
          )}

          {/* Show error message in conversation when there are existing messages */}
          {chat.error && messages.length > 0 && (
            <MessageContainer>
              <StyledErrorIcon sx={{ fontSize: 24, color: "error.main" }} />
              <MessageBubble
                sx={(theme: Theme) => ({
                  backgroundColor: `${theme.palette.error[500]} !important`,
                  color: "white !important",
                })}
              >
                <Typography variant="body2" color="inherit" gutterBottom>
                  An error occurred while processing your request.
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: "white",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: "bold",
                    "&:hover": { textDecoration: "none" },
                  }}
                  onClick={handleTryAgain}
                >
                  Try again
                </Typography>
              </MessageBubble>
            </MessageContainer>
          )}

          {/* Scroll target for auto-scrolling */}
          <div ref={messagesEndRef} />
        </MessagesArea>
      </ChatMessagesContainer>

      {/*  Chat message input container and form */}
      <ChatInputContainer>
        <form id="message-input" onSubmit={handleSendMessage}>
          <ChatMessageInputContainer container spacing={1} direction="row" wrap="nowrap">
            <ChatMessageInput
              id="chat-message-input"
              placeholder="Type your message here"
              value={input}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
              fullWidth
              autoFocus
            />

            <SendChatMessageIconButton
              id="send-chat-message-icon-button"
              aria-label="Send message"
              type="submit"
              disabled={isProcessingResponse}
              sx={{ marginTop: "2px" }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </SendChatMessageIconButton>
          </ChatMessageInputContainer>
        </form>
      </ChatInputContainer>
    </>
  );
}
